import React, { useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogTitle from '@mui/material/DialogTitle';
import { Grid, Box, Alert, CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  updateBarberAddress,
  updateBarberLocation,
} from '../../middleware/registration_service/updateRequest';
import { getGeoCode } from '../../middleware/registration_service/getRequests';
import { parseGeocodeResponse } from '../../utils/geocodingUtils';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { useAuth0 } from '@auth0/auth0-react';
import { useProfileData } from '../../context/ProfileDataContext';

const schema = Yup.object().shape({
  addressLineOne: Yup.string().required('Address line 1 is required'),
  addressLineTwo: Yup.string(),
  city: Yup.string().required('City is required'),
  country: Yup.string().required('Country is required'),
  postcode: Yup.string().required('Postcode is required'),
});

export default function BarberAddressDialog() {
  const { barberId, barber, setBarber } = useProfileData();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();

  // Pre-populate the form with the barber's currently stored address so the
  // dialog never opens blank.
  const initialValues = useMemo(
    () => ({
      addressLineOne: barber?.addressLineOne || '',
      addressLineTwo: barber?.addressLineTwo || '',
      city: barber?.city || '',
      postcode: barber?.postcode || '',
      country: barber?.country || '',
    }),
    [barber]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        setSubmitted(true);
        setError(null);
        setLoading(true);

        const token = await getAccessTokenSilently();

        await updateBarberAddress(
          values.addressLineOne,
          values.addressLineTwo,
          values.city,
          values.postcode,
          values.country,
          barberId,
          token
        );

        // Reflect the updated address immediately on the profile without a
        // full page reload.
        setBarber((prev) => (prev ? { ...prev, ...values } : prev));

        // Re-geocode the updated address and persist coordinates
        // (best-effort; a geocoding failure should not block the address
        // update itself).
        try {
          const address = [
            values.addressLineOne,
            values.addressLineTwo,
            values.city,
            values.postcode,
            values.country,
          ]
            .filter(Boolean)
            .join(', ');
          const geocodeRes = await getGeoCode(address);
          const coordinates = parseGeocodeResponse(geocodeRes.data);
          if (coordinates) {
            await updateBarberLocation(
              barberId,
              coordinates.lat,
              coordinates.lng
            );
          } else {
            console.warn('Could not resolve coordinates for barber address.');
          }
        } catch (geoErr) {
          console.error('Failed to geocode/save barber location:', geoErr);
        }

        setTimeout(() => {
          setSubmitted(false);
          setLoading(false);
          handleClose();
        }, 3000);
      } catch (err) {
        console.error('Error updating address:', err);
        setError('Error updating address');
        setSubmitted(false);
        setLoading(false);
      }
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSubmitted(false);
    setError(null);
    formik.resetForm();
  };

  return (
    <React.Fragment>
      <IconButton
        aria-label="Edit barber address"
        onClick={handleClickOpen}
        size="small"
        sx={{ color: '#dd8709', ml: 0.5 }}
      >
        <EditIcon sx={{ width: '18px', height: '18px' }} />
      </IconButton>

      <Dialog
        className="barberProfileDialog"
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDialog-paper': {
            width: '80%',
            maxWidth: '600px',
          },
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              textAlign: 'center',
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
            id="customized-dialog-title"
          >
            <LocationOnOutlinedIcon sx={{ color: '#dd8709' }} />
            Change Address
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="addressLineOne"
                    fullWidth
                    id="addressLineOne"
                    label="Address1"
                    value={formik.values.addressLineOne}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.addressLineOne &&
                      Boolean(formik.errors.addressLineOne)
                    }
                    helperText={
                      formik.touched.addressLineOne &&
                      formik.errors.addressLineOne
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="addressLineTwo"
                    label="Address2"
                    name="addressLineTwo"
                    value={formik.values.addressLineTwo}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.addressLineTwo &&
                      Boolean(formik.errors.addressLineTwo)
                    }
                    helperText={
                      formik.touched.addressLineTwo &&
                      formik.errors.addressLineTwo
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="city"
                    label="City"
                    name="city"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="country"
                    label="Country"
                    name="country"
                    value={formik.values.country}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.country && Boolean(formik.errors.country)
                    }
                    helperText={formik.touched.country && formik.errors.country}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    fullWidth
                    id="postcode"
                    label="Postcode"
                    name="postcode"
                    value={formik.values.postcode}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.postcode && Boolean(formik.errors.postcode)
                    }
                    helperText={
                      formik.touched.postcode && formik.errors.postcode
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              className="buttonHover"
              type="submit"
              disabled={loading}
              fullWidth
              variant="contained"
              sx={{
                mt: '2em',
                mr: '15px',
                ml: '15px',
                mb: '10px',
                bgcolor: '#dd8709',
                color: '#fff',
                fontFamily: 'GFS Didot',
                fontWeight: 'bold',
                p: 1,
                width: '100%',
                '&:hover': { bgcolor: '#b86e08' },
                '&.Mui-disabled': { bgcolor: '#e0b96a', color: '#fff' },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Save'
              )}
            </Button>
          </DialogActions>
        </form>
        {submitted && (
          <Alert
            onClose={() => setSubmitted(false)}
            severity="success"
            sx={{ mt: '0.5em' }}
          >
            Profile updated successfully
          </Alert>
        )}
        {error && (
          <Alert
            onClose={() => setError(null)}
            severity="error"
            sx={{ mt: '0.5em' }}
          >
            {error}
          </Alert>
        )}
      </Dialog>
    </React.Fragment>
  );
}
